/* ---------------------------------------------------------------------------
 * MD Fashionwear — catalogue pool for "You may also love"
 *
 * THE MARKET RULE, AND IT IS THE OPPOSITE OF WHAT YOU MIGHT ASSUME
 * Worldwide must NOT be shown in-stock items. In-stock stock sits in the Lebanon
 * local warehouse, whose delivery profile ("Available Shipping") has only a
 * Lebanon zone — those pieces physically cannot ship anywhere else. Worldwide is
 * served from the overseas/dropship catalogue. In-stock is identified by the
 * ONLINE tag, the convention already used in snippets/line-item.liquid.
 *
 * Measured: 1,109 products, 342 tagged ONLINE, 766 preorder, 3 both, 4 neither.
 * Excluding ONLINE leaves 767 for worldwide. Products tagged BOTH are withheld
 * from worldwide — shipping something that may sit in Beirut is the worse error.
 *
 * LEBANON MUST SEE BOTH KINDS — WHY THE WALK DOES NOT STOP EARLY
 * ONLINE products are only 31% of the catalogue and they cluster on particular
 * pages. Stopping as soon as 60 products had been collected meant a single page
 * could supply them all, and a Lebanese shopper saw 24 preorder items and no
 * in-stock ones at all. For Lebanon the walk continues until it has a minimum of
 * BOTH kinds, or runs out of pages.
 *
 * WHY mix() EXISTS
 * gather() reads pages in catalogue order and pages are grouped, so the first 24
 * were near-identical every time and often all one category. mix() shuffles, then
 * round-robins across product types so a row is dresses AND shoes AND bags AND
 * accessories. For Lebanon it also alternates in-stock with preorder.
 *
 * ---------------------------------------------------------------------------
 * SPEED — WHY THE PAGES ARE CACHED. Added 30 Jul 2026.
 *
 * The carousel rerolls every time the drawer opens, and every reroll called
 * gather(), which re-fetched up to five /products.json pages of 250 products
 * each — roughly 180 KB per page — ONE AFTER ANOTHER. Measured cold, that walk
 * ran from 2.6 s to 9.2 s. The old cards stayed on screen until it finished, so
 * the row visibly lagged behind the drawer opening. The selection was never
 * wrong; it just arrived late.
 *
 * Two changes, no change to what gets selected:
 *   1. Pages are cached, in memory and in sessionStorage, so the second and
 *      every later open costs no network at all and the reroll is pure CPU.
 *   2. The pages still needed are fetched IN PARALLEL, in at most two waves,
 *      instead of five sequential round trips.
 *
 * The shuffle that gives the carousel its variety happens in mix(), not in the
 * fetching, so caching pages does NOT make the row repeat itself — a cached
 * pool is reshuffled and re-round-robined on every single open.
 *
 * If sessionStorage is full the save is skipped and everything still works; it
 * just costs a refetch on the next page load. Never let it throw.
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var MAX_PAGES = 5;
  var PER_KIND = 12;      // Lebanon: minimum of in-stock AND of preorder
  var FIRST_WAVE = 2;     // fetched together before deciding whether more is needed

  var PAGE_KEY = 'md_cat_pages_v1';
  var PAGE_TTL = 30 * 60 * 1000;

  var memPages = {};      // page number -> raw product array
  var inflight = {};      // page number -> Promise, so two opens never double-fetch

  function norm(t) {
    return String(t).toLowerCase().replace(/[^a-z]/g, '');
  }

  function hasTag(p, tag) {
    var tags = p.tags || [];
    for (var i = 0; i < tags.length; i++) if (norm(tags[i]) === tag) return true;
    return false;
  }

  function isLocalStock(p) { return hasTag(p, 'online'); }
  function isPreorder(p) { return hasTag(p, 'preorder'); }

  // MD 7 Sep 2026 - Sleepwear is Lebanon only. This pool is built client-side from
  // products.json, so the Liquid gate in snippets/md-market-visibility.liquid cannot
  // reach it; the rule has to be repeated here. Shapewear is NOT affected - it carries
  // the 'shapewear' tag, not 'Sleepwear'. Both tag spellings on the products are
  // checked because norm() only lowercases, it does not singularise.
  function isLebanonOnlyCategory(p) {
    return hasTag(p, 'sleepwear') || hasTag(p, 'sleepwears');
  }

  function sellable(p) {
    return (p.variants || []).filter(function (v) { return v.available; }).length > 0;
  }

  function blocked(p, worldwide) {
    if (!sellable(p)) return true;
    if (worldwide && isLocalStock(p)) return true;
    if (worldwide && isLebanonOnlyCategory(p)) return true;
    return false;
  }

  function shuffle(a) {
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  // product_type is blank on many products, so fall back to a recognisable tag.
  var TAG_TYPES = ['shoes', 'bags', 'bag', 'accessories', 'accessory', 'jewelry',
                   'dress', 'dresses', 'set', 'sets', 'top', 'tops', 'jeans',
                   'bottoms', 'swim', 'active', 'outerwear', 'skirt'];

  function typeKey(p) {
    var t = String(p.product_type || '').trim().toLowerCase();
    if (t) return t;

    var tags = (p.tags || []).map(norm);
    for (var i = 0; i < TAG_TYPES.length; i++) {
      if (tags.indexOf(TAG_TYPES[i]) > -1) return TAG_TYPES[i];
    }
    return 'other';
  }

  function roundRobin(list) {
    var buckets = {};
    list.forEach(function (p) {
      var k = typeKey(p);
      (buckets[k] = buckets[k] || []).push(p);
    });

    var keys = shuffle(Object.keys(buckets));
    keys.forEach(function (k) { shuffle(buckets[k]); });

    var out = [];
    var more = true;
    while (more) {
      more = false;
      for (var i = 0; i < keys.length; i++) {
        var b = buckets[keys[i]];
        if (b.length) { out.push(b.shift()); more = true; }
      }
    }
    return out;
  }

  function mix(list, worldwide) {
    if (!list || !list.length) return [];
    shuffle(list);

    if (worldwide) return roundRobin(list);

    var inStock = roundRobin(list.filter(isLocalStock));
    var other = roundRobin(list.filter(function (p) { return !isLocalStock(p); }));

    var out = [];
    var i = 0, j = 0;
    while (i < inStock.length || j < other.length) {
      if (i < inStock.length) out.push(inStock[i++]);
      if (j < other.length) out.push(other[j++]);
    }
    return out;
  }

  /* ---- page cache ------------------------------------------------------ */

  function loadCache() {
    try {
      var o = JSON.parse(sessionStorage.getItem(PAGE_KEY) || 'null');
      if (!o || Date.now() - o.t > PAGE_TTL) return {};
      return o.p || {};
    } catch (e) { return {}; }
  }

  function saveCache() {
    try {
      sessionStorage.setItem(PAGE_KEY, JSON.stringify({ t: Date.now(), p: memPages }));
    } catch (e) {
      /* Quota. Costs a refetch next page load, nothing more. */
    }
  }

  memPages = loadCache();

  function page(n) {
    if (memPages[n]) return Promise.resolve(memPages[n]);
    if (inflight[n]) return inflight[n];

    inflight[n] = fetch('/products.json?limit=250&page=' + n)
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (j) {
        var list = (j && j.products) || [];
        memPages[n] = list;
        saveCache();
        delete inflight[n];
        return list;
      })
      .catch(function () {
        delete inflight[n];
        return [];   // not cached: a network blip must not poison the pool
      });

    return inflight[n];
  }

  /**
   * gather(opts) -> Promise<Array>   already mixed, ready to slice
   *   opts.worldwide  true to withhold Lebanon local stock
   *   opts.inCart     product ids already in the cart
   *   opts.excluded   map of product ids already added this session
   *   opts.need       target size for worldwide
   */
  function gather(opts) {
    var worldwide = !!opts.worldwide;
    var inCart = opts.inCart || [];
    var excluded = opts.excluded || {};
    var need = opts.need || 48;

    var order = shuffle([1, 2, 3, 4, 5]).slice(0, MAX_PAGES);
    var out = [];
    var seen = {};
    var localCount = 0;
    var otherCount = 0;

    function absorb(list) {
      (list || []).forEach(function (p) {
        var id = String(p.id);
        if (seen[id]) return;
        if (blocked(p, worldwide)) return;
        if (inCart.indexOf(id) > -1) return;
        if (excluded[id]) return;
        seen[id] = 1;
        out.push(p);
        if (isLocalStock(p)) localCount++; else otherCount++;
      });
    }

    function enough() {
      if (worldwide) return out.length >= need;
      // Lebanon needs a real supply of both kinds, not just a total.
      return localCount >= PER_KIND && otherCount >= PER_KIND;
    }

    function done() { return mix(out, worldwide); }

    /* Anything already cached is free, so use all of it before touching the
       network. After the first open this alone is normally the whole job. */
    var pending = [];
    order.forEach(function (n) {
      if (memPages[n]) absorb(memPages[n]);
      else pending.push(n);
    });

    if (enough() || !pending.length) return Promise.resolve(done());

    var wave1 = pending.slice(0, FIRST_WAVE);
    var wave2 = pending.slice(FIRST_WAVE);

    return Promise.all(wave1.map(page)).then(function (lists) {
      lists.forEach(absorb);
      if (enough() || !wave2.length) return done();

      return Promise.all(wave2.map(page)).then(function (more) {
        more.forEach(absorb);
        return done();
      });
    });
  }

  /* Warm the pool quietly after the page settles, so the first drawer open is
     already free. Idle only — it must never compete with the page itself. */
  function warm() {
    var run = function () { page(1); page(2); };
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 4000 });
    else setTimeout(run, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', warm);
  } else {
    warm();
  }

  window.MDCartPool = {
    gather: gather,
    mix: mix,
    blocked: blocked,
    isLocalStock: isLocalStock,
    isPreorder: isPreorder,
    sellable: sellable,
    typeKey: typeKey
  };
})();
